import {
  ArrowLeftRight,
  BadgeCheck,
  Banknote,
  Briefcase,
  Bell,
  BookOpen,
  Bot,
  Bug,
  Building2,
  CheckSquare,
  ClipboardList,
  CreditCard,
  FileSignature,
  Globe,
  FileText,
  FileWarning,
  FlaskConical,
  Gift,
  HandCoins,
  Landmark,
  Layers,
  LayoutDashboard,
  LifeBuoy,
  Lightbulb,
  LucideIcon,
  MapPin,
  MessageCircle,
  PackageSearch,
  ReceiptText,
  ScanSearch,
  Scale,
  Send,
  Settings,
  Shield,
  ShieldAlert,
  ShieldCheck,
  ShoppingBag,
  Siren,
  SlidersHorizontal,
  Sparkles,
  Store,
  Tag,
  TrendingUp,
  UserPlus,
  Users,
  PiggyBank,
} from 'lucide-react';

/**
 * The console's information architecture, in one place.
 *
 * This used to be an array inline in `Sidebar.tsx`, and it had gone the way
 * those arrays go: a "System" group with twenty-six children holding nine
 * business pages, six compliance queues, plans, config, monitoring, admins
 * and settings. A business-operations admin navigated their entire job
 * through a menu called *System*.
 *
 * Two rules keep it from happening again:
 *
 * 1. **A section is a subject, not a leftover.** "Consumer", "Business",
 *    "Marketplace" name who the pages are about. There is no bin.
 * 2. **Every route in `App.tsx` appears here exactly once.** A page reachable
 *    from nowhere is a page nobody maintains — `navigation.test.ts` fails the
 *    build if the two ever disagree.
 *
 * The sidebar renders this two levels at a time: root menu, then one
 * section with a back button. Nothing nests deeper, so there is nowhere for
 * a twenty-six-item list to reappear.
 */

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Permission key required to see this item. Omit → always visible. */
  permission?: string;
}

export interface NavSection {
  key: string;
  label: string;
  icon: LucideIcon;
  items: NavItem[];
}

export const NAVIGATION: NavSection[] = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    items: [
      { to: '/dashboard', label: 'Overview', icon: LayoutDashboard, permission: 'analytics.view' },
      // What PayIQ earns in every app against what it pays providers, the
      // people it rewards and its fixed costs. Replaced "Revenue", which
      // counted Safe Haven's commission, VAT and stamp duty as income.
      { to: '/finance', label: 'Money in and out', icon: Scale, permission: 'finance.view' },
      // Sign-ups from the marketing site's "Join waitlist", for both apps.
      // Here rather than under Consumer or Business because it is both, and
      // because it is a launch number, not a list of customers. `users.view`
      // because that is what the endpoint requires: names, emails, phones.
      { to: '/waitlist', label: 'Waitlist', icon: UserPlus, permission: 'users.view' },
      // People and businesses who tapped "Join waitlist" in the apps for a
      // card that says "Coming soon". Beside the marketing waitlist because
      // it is the same kind of number, and for both apps; apart from it
      // because these are accounts and can be acted on.
      { to: '/card-waitlist', label: 'Card Waitlist', icon: CreditCard, permission: 'users.view' },
    ],
  },
  {
    key: 'consumer',
    label: 'Consumer',
    icon: Users,
    items: [
      { to: '/users', label: 'Users', icon: Users, permission: 'users.view' },
      { to: '/tier3-verifications', label: 'Tier-3 Verifications', icon: BadgeCheck, permission: 'users.kyc' },
      { to: '/transactions', label: 'Transactions', icon: ArrowLeftRight, permission: 'transactions.view' },
      // Each person's savings account against what their goals hold, and
      // the moves between main and savings the bank never answered for.
      { to: '/savings-reconciliation', label: 'Savings', icon: PiggyBank, permission: 'savings_reconciliation.view' },
      // A person's airtime and bills against what Safe Haven took, and the
      // ledger repairs that put people's history right — the business
      // Reconciliation page only ever reached businesses.
      { to: '/consumer-reconciliation', label: 'Reconciliation', icon: Scale, permission: 'consumer_reconciliation.view' },
      { to: '/payment-requests', label: 'Payment Requests', icon: Send, permission: 'payment_requests.view' },
      { to: '/referrals', label: 'Referrals', icon: Gift, permission: 'referrals.view' },
      // People who bring businesses to PayIQ Business and earn a share of
      // what PayIQ makes from them. Applied for from Invite friends.
      { to: '/aggregators', label: 'Aggregators', icon: Briefcase, permission: 'aggregators.view' },
      { to: '/ajo', label: 'Ajo', icon: HandCoins, permission: 'ajo.view' },
    ],
  },
  {
    key: 'business',
    label: 'Business',
    icon: Building2,
    items: [
      { to: '/businesses', label: 'Businesses', icon: Building2, permission: 'business_kyb.view' },
      // Same permission as the consumer list, and the same endpoint — the
      // two differ only by which side of the `transactions` CHECK
      // constraint they read. An admin who cannot see one cannot see the
      // other, which is what the API enforces anyway.
      { to: '/business-transactions', label: 'Transactions', icon: ArrowLeftRight, permission: 'transactions.view' },
      { to: '/business-registrations', label: 'CAC Registrations', icon: FileSignature, permission: 'business_registrations.view' },
      { to: '/business-accounts', label: 'Business Accounts', icon: Landmark, permission: 'business_accounts.backfill' },
      { to: '/business-invoices', label: 'Business Invoices', icon: ReceiptText, permission: 'business_invoices.view' },
      { to: '/business-reconciliation', label: 'Reconciliation', icon: Scale, permission: 'business_reconciliation.view' },
      // Sits with Business rather than Marketplace: the subject of the
      // review is a company and its paperwork, not the shop it lists on.
      { to: '/storefront-review', label: 'Storefront Review', icon: Store, permission: 'storefront.review' },
      { to: '/storefront-domains', label: 'Shop Domains', icon: Globe, permission: 'storefront.review' },
    ],
  },
  {
    key: 'marketplace',
    label: 'Marketplace',
    icon: Store,
    items: [
      { to: '/vendors', label: 'Vendors', icon: Store, permission: 'shop.vendors' },
      // Was a second item called "Transactions", which made the sidebar
      // show that word twice with no way to tell which was which. These are
      // escrow holds against shop orders; "Orders" is what they are.
      { to: '/shop-transactions', label: 'Orders', icon: ShoppingBag, permission: 'shop.orders' },
      { to: '/product-requests', label: 'Product Requests', icon: PackageSearch, permission: 'product_requests.view' },
      { to: '/shop-categories', label: 'Categories', icon: Tag, permission: 'shop.vendors' },
    ],
  },
  {
    key: 'compliance',
    label: 'Compliance & Risk',
    icon: ShieldCheck,
    items: [
      { to: '/approvals', label: 'Approvals', icon: CheckSquare, permission: 'approvals.view' },
      { to: '/compliance/cases', label: 'Compliance Cases', icon: ShieldAlert, permission: 'compliance.view' },
      { to: '/compliance/screening', label: 'Screening Review', icon: ScanSearch, permission: 'compliance.view' },
      { to: '/compliance/reports', label: 'Regulatory Reports', icon: FileWarning, permission: 'compliance.view' },
      // The next three are gated on `users.kyc` rather than
      // `compliance.view`: each asks "is this document about this person",
      // which is the KYC reviewer's judgement and the permission the
      // endpoints themselves require. A sidebar that disagreed with the API
      // would show links that 403.
      { to: '/compliance/account-recovery', label: 'Account Recovery', icon: LifeBuoy, permission: 'users.kyc' },
      { to: '/compliance/signing-mandates', label: 'Signing Mandates', icon: FileSignature, permission: 'users.kyc' },
      { to: '/compliance/cac-claims', label: 'CAC Claims', icon: Landmark, permission: 'users.kyc' },
      { to: '/risk', label: 'Risk & Fraud', icon: Siren, permission: 'fraud.view' },
    ],
  },
  {
    key: 'support',
    label: 'Support',
    icon: MessageCircle,
    items: [
      { to: '/support', label: 'Support Chat', icon: MessageCircle, permission: 'support.manage' },
      { to: '/help-centre', label: 'Help Centre', icon: BookOpen, permission: 'content.view' },
      { to: '/content', label: 'Content Pages', icon: FileText, permission: 'content.view' },
      { to: '/notifications', label: 'Notifications', icon: Bell, permission: 'notifications.view' },
    ],
  },
  {
    key: 'ibra',
    label: 'Ibra',
    icon: Bot,
    items: [
      { to: '/agents', label: 'AI Agents', icon: Bot, permission: 'agents.view' },
      { to: '/knowledge-base', label: 'Knowledge Base', icon: BookOpen, permission: 'knowledge_gaps.view' },
      { to: '/knowledge-gaps', label: 'Knowledge Gaps', icon: Lightbulb, permission: 'knowledge_gaps.view' },
      { to: '/ibra-evals', label: 'Evaluations', icon: FlaskConical, permission: 'analytics.view' },
      { to: '/ibra-nuggets', label: 'Feature Nuggets', icon: Sparkles, permission: 'analytics.view' },
      { to: '/money-figures', label: 'Money Figures', icon: TrendingUp, permission: 'content.view' },
    ],
  },
  {
    key: 'platform',
    label: 'Platform',
    icon: SlidersHorizontal,
    items: [
      { to: '/plans', label: 'Plans & Pricing', icon: Layers, permission: 'plans.view' },
      { to: '/config', label: 'Configuration', icon: Settings, permission: 'config.view' },
      { to: '/ai-settings', label: 'AI Settings', icon: Bot, permission: 'ai_settings.view' },
      { to: '/error-monitoring', label: 'Error Monitoring', icon: Bug, permission: 'monitoring.view' },
      { to: '/bill-addresses', label: 'Bill Addresses', icon: MapPin, permission: 'bills.backfill' },
    ],
  },
  {
    key: 'administration',
    label: 'Administration',
    icon: Shield,
    items: [
      { to: '/admins', label: 'Admin Management', icon: Shield, permission: 'admins.manage' },
      { to: '/roles', label: 'Roles & Permissions', icon: ShieldCheck, permission: 'roles.manage' },
      // PayIQ's own staff roster and pay runs, not a customer's payroll —
      // which is why it belongs here rather than under Business.
      { to: '/payroll', label: 'Payroll', icon: Banknote, permission: 'payroll.view' },
      { to: '/audit-logs', label: 'Audit Logs', icon: ClipboardList, permission: 'audit_logs.view' },
    ],
  },
];

/** Whether `pathname` is this link's page, or a page beneath it. */
export function isRouteActive(pathname: string, target: string) {
  return pathname === target || pathname.startsWith(`${target}/`);
}

/**
 * The section a path belongs to, so a deep link opens its own section
 * rather than the root menu.
 *
 * Longest match wins. `/compliance/cases` would otherwise be ambiguous the
 * moment somebody adds a `/compliance` link, and the more specific one is
 * always the page actually being shown.
 */
export function sectionForPath(pathname: string): NavSection | null {
  let best: { section: NavSection; length: number } | null = null;
  for (const section of NAVIGATION) {
    for (const item of section.items) {
      if (!isRouteActive(pathname, item.to)) continue;
      if (!best || item.to.length > best.length) {
        best = { section, length: item.to.length };
      }
    }
  }
  return best?.section ?? null;
}
